/**
 * AdminAthletesImportsPage — /admin/athletes/imports (V9.15 Sprint 3 Lot A.2)
 *
 * Interface institutionnelle admin/COPF pour l'import massif d'athlètes.
 * 4 vues : Nouvel import · Imports récents · Doublons · Revendications.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, X, RefreshCw,
  ShieldAlert, ArrowLeft, RotateCcw, Users, ClipboardList, UserCheck, Copy, GitMerge, FileDown,
  Sparkles, Award,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminAthletesImportsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isModerator = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [tab, setTab] = useState("new");

  if (!isModerator) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="import-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">L'import est réservé aux admins et à la COPF.</p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/")}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />Retour
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-imports-page">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />Imports athlètes
        </h1>
        <p className="text-sm text-muted-foreground">Amorcer et maintenir l'annuaire AITO.PF à partir de fichiers Excel/CSV — matching déterministe, preview obligatoire, rollback disponible.</p>
      </div>

      <AthletesDashboardWidget />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="imports-tabs" className="w-full justify-start overflow-x-auto no-scrollbar">
          <TabsTrigger value="new" data-testid="imports-tab-new"><Upload className="h-3.5 w-3.5 mr-1.5" />Nouvel import Athlètes</TabsTrigger>
          <TabsTrigger value="multi" data-testid="imports-tab-multi"><Users className="h-3.5 w-3.5 mr-1.5" />Import multi-entités</TabsTrigger>
          <TabsTrigger value="batches" data-testid="imports-tab-batches"><ClipboardList className="h-3.5 w-3.5 mr-1.5" />Imports récents</TabsTrigger>
          <TabsTrigger value="claims" data-testid="imports-tab-claims"><UserCheck className="h-3.5 w-3.5 mr-1.5" />Revendications</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4"><NewImportView /></TabsContent>
        <TabsContent value="multi" className="mt-4"><MultiEntityImportView /></TabsContent>
        <TabsContent value="batches" className="mt-4"><BatchesView /></TabsContent>
        <TabsContent value="claims" className="mt-4"><ClaimsView /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Nouvel import ----------
function NewImportView() {
  const [file, setFile] = useState(null);
  const [sourceKey, setSourceKey] = useState("");
  const [mode, setMode] = useState("add_only");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onProbable, setOnProbable] = useState("propose");
  const [onAbsent, setOnAbsent] = useState("keep");
  // V9.21 — inclure les achievements palmarès structurés par IA
  const [includePalmaresAi, setIncludePalmaresAi] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/athletes/imports/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modele-import-athletes-AITO.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showError(e, { prefix: "Modèle :" }); }
  };

  // V9.20 — Auto-suggère un source_key propre à partir du nom de fichier
  //         dès qu'un fichier est sélectionné ET que le champ est vide.
  const onFileChange = (f) => {
    setFile(f);
    setValidationError(null);
    if (f && !sourceKey.trim()) {
      const base = (f.name || "").replace(/\.[^.]+$/, "");
      const suggested = base
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60);
      if (suggested) setSourceKey(suggested);
    }
  };

  const runPreview = async () => {
    // V9.20 — Le bouton reste TOUJOURS cliquable ; on affiche une vraie erreur si champ manquant.
    setValidationError(null);
    if (!file) {
      setValidationError("Sélectionnez d'abord un fichier XLSX ou CSV.");
      toast.error("Sélectionnez un fichier");
      return;
    }
    const cleaned = (sourceKey || "").trim();
    if (!cleaned) {
      setValidationError("Le champ « source_key » est obligatoire (ex : COPF_ROAD2027_ATHLETES).");
      toast.error("Le champ source_key est requis");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/athletes/imports/preview?source_key=${encodeURIComponent(cleaned)}&mode=${mode}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) { showError(e, { prefix: "Preview :" }); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview?.preview_id) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/athletes/imports/commit", {
        preview_id: preview.preview_id,
        on_probable: onProbable,
        on_absent: onAbsent,
        include_palmares_ai: includePalmaresAi,
      });
      setResult(data);
      setPreview(null);
      setFile(null);
      // V9.20 — Bilan détaillé : on prend en compte les erreurs si l'API renvoie des invalid > 0
      const c = data.counts || {};
      const success = (c.created || 0) + (c.updated || 0);
      const failed = (c.invalid || 0) + (c.conflicts || 0);
      if (failed > 0) {
        toast.warning(
          `Import partiel : ${success} traité${success > 1 ? "s" : ""}, ${failed} à revoir. Consultez le détail ci-dessous.`
        );
      } else {
        toast.success(`Import réussi : ${c.created || 0} créé${(c.created || 0) > 1 ? "s" : ""}, ${c.updated || 0} mis à jour.`);
      }
      // V9.20 — Rafraîchit les compteurs du dashboard admin
      try {
        window.dispatchEvent(new CustomEvent("aito:athletes-refresh"));
      } catch { /* ignore */ }
    } catch (e) { showError(e, { prefix: "Import :" }); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-4">
      {/* Étape 1 : modèle */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="font-display font-semibold">1. Télécharger le modèle</h2>
            <p className="text-xs text-muted-foreground">Modèle Excel officiel avec 3 feuilles : ATHLETES_IMPORT · REFERENTIELS · MODE_EMPLOI.</p>
          </div>
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="imports-download-template">
            <Download className="h-3.5 w-3.5 mr-1.5" />Télécharger le modèle XLSX
          </Button>
        </div>
      </Card>

      {/* Étape 2 : upload + params */}
      <Card className="p-4 space-y-3">
        <h2 className="font-display font-semibold">2. Charger le fichier</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Fichier XLSX / CSV</Label>
            <Input type="file" accept=".xlsx,.xlsm,.csv,.txt" onChange={(e) => onFileChange(e.target.files?.[0] || null)} data-testid="imports-file" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Mode <span className="text-destructive">*</span></Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="imports-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="add_only">Ajouter uniquement</SelectItem>
                <SelectItem value="sync">Synchroniser (recommandé)</SelectItem>
                <SelectItem value="replace">Remplacer le lot précédent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">source_key (série métier suivie dans le temps) <span className="text-destructive">*</span></Label>
          <Input
            value={sourceKey}
            onChange={(e) => { setSourceKey(e.target.value); setValidationError(null); }}
            placeholder="COPF_ROAD2027_ATHLETES"
            data-testid="imports-source-key"
            className={validationError && !sourceKey.trim() ? "border-destructive" : ""}
          />
          <p className="text-[11px] text-muted-foreground">
            Ex : COPF_ROAD2027_ATHLETES, FEDERATION_JUDO_LICENCIES_2026. Doit rester stable entre imports successifs.
            {file && !sourceKey.trim() && (
              <span className="ml-1 text-[hsl(var(--aito-lagoon))]">Astuce : auto-rempli depuis le nom du fichier.</span>
            )}
          </p>
        </div>
        {validationError && (
          <p className="text-xs text-destructive font-medium flex items-center gap-1" data-testid="imports-validation-error">
            <AlertTriangle className="h-3.5 w-3.5" /> {validationError}
          </p>
        )}
        <Button
          onClick={runPreview}
          disabled={busy}
          data-testid="imports-run-preview"
          className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
        >
          {busy ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}
          Analyser le fichier
        </Button>
      </Card>

      {/* Étape 3 : preview */}
      {preview && (
        <Card className="p-4 space-y-3" data-testid="imports-preview-card">
          <h2 className="font-display font-semibold">3. Prévisualisation</h2>
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-6 text-sm">
            <Stat label="Lignes" value={preview.counts.total_lines} />
            <Stat label="Nouveaux" value={preview.counts.new} color="emerald" />
            <Stat label="Existants (certain)" value={preview.counts.match_certain} />
            <Stat label="Doublons potentiels" value={preview.counts.match_probable} color="amber" />
            <Stat label="Invalides" value={preview.counts.invalid} color="red" />
            <Stat label="Absents du fichier" value={preview.counts.absent_in_previous} />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Action pour "doublons potentiels"</Label>
              <Select value={onProbable} onValueChange={setOnProbable}>
                <SelectTrigger data-testid="imports-on-probable"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="propose">Proposer (revue manuelle)</SelectItem>
                  <SelectItem value="update">Mettre à jour l'existant</SelectItem>
                  <SelectItem value="ignore">Ignorer la ligne</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(mode === "sync" || mode === "replace") && (
              <div className="space-y-1">
                <Label className="text-xs">Action pour "présents précédemment, absents maintenant"</Label>
                <Select value={onAbsent} onValueChange={setOnAbsent}>
                  <SelectTrigger data-testid="imports-on-absent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep">Conserver (recommandé)</SelectItem>
                    <SelectItem value="untag_road2027">Retirer road_2027</SelectItem>
                    <SelectItem value="archive">Archiver (soft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {preview.invalid.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-destructive"><AlertTriangle className="inline h-3 w-3 mr-1" />{preview.invalid.length} ligne(s) invalides</summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc max-h-40 overflow-y-auto">
                {preview.invalid.map((iv, i) => (
                  <li key={i}>Ligne {iv.line} : {iv.errors.join(", ")}</li>
                ))}
              </ul>
            </details>
          )}

          {/* V9.21 — Propositions IA palmarès */}
          <PalmaresAiPanel
            preview={preview}
            includePalmaresAi={includePalmaresAi}
            onToggleInclude={setIncludePalmaresAi}
          />

          <div className="flex justify-end">
            <Button onClick={commit} disabled={committing} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="imports-commit">
              {committing ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Confirmer et importer
            </Button>
          </div>
        </Card>
      )}

      {/* Étape 4 : résultat */}
      {result && (
        <Card className="p-4 space-y-3 border-emerald-500/40" data-testid="imports-result-card">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="font-display font-semibold">Import terminé</h2>
          </div>
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-6 text-sm">
            <Stat label="Total" value={result.counts.total || 0} />
            <Stat label="Créés" value={result.counts.created} color="emerald" />
            <Stat label="Mis à jour" value={result.counts.updated} />
            <Stat label="Ignorés" value={result.counts.skipped} />
            <Stat label="Conflits" value={result.counts.conflicts} color="amber" />
            <Stat label="Invalides" value={result.counts.invalid} color="red" />
          </div>
          {(result.counts.users_linked_existing > 0 || result.counts.users_created_shell > 0) && (
            <div className="rounded-md bg-secondary/50 border p-2 text-xs">
              <strong>Comptes utilisateurs :</strong>{" "}
              {result.counts.users_linked_existing || 0} lié{(result.counts.users_linked_existing || 0) > 1 ? "s" : ""} à des User existants
              (rôle « athlete » ajouté sans écraser les autres rôles) ·{" "}
              {result.counts.users_created_shell || 0} compte-coquille{(result.counts.users_created_shell || 0) > 1 ? "s" : ""} créé
              {(result.counts.users_created_shell || 0) > 1 ? "s" : ""} (revendicable par email — aucun mot de passe généré).
            </div>
          )}
          <p className="text-xs text-muted-foreground">Batch ID : <span className="font-mono">{result.batch_id}</span></p>
          {Array.isArray(result.rows) && result.rows.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/60 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2 w-14">Ligne</th>
                      <th className="p-2">Athlète</th>
                      <th className="p-2 w-28">Statut</th>
                      <th className="p-2">Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => (
                      <tr key={i} className="border-t" data-testid={`imports-row-${r.line}`}>
                        <td className="p-2 font-mono">{r.line}</td>
                        <td className="p-2">
                          {r.slug ? (
                            <Link to={`/athletes/${r.slug}`} className="text-[hsl(var(--aito-lagoon))] hover:underline">{r.full_name}</Link>
                          ) : r.full_name}
                          {r.email && <div className="text-[10px] text-muted-foreground">{r.email}</div>}
                        </td>
                        <td className="p-2">
                          <Badge
                            variant="outline"
                            className={
                              r.status === "created" ? "border-emerald-500 text-emerald-700" :
                              r.status === "updated" ? "border-blue-500 text-blue-700" :
                              r.status === "error" ? "border-red-500 text-red-700" :
                              r.status === "conflict" ? "border-amber-500 text-amber-700" :
                              "border-muted"
                            }
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-muted-foreground">
                          {r.reason || (r.user_event === "user_linked_existing" ? "→ User existant lié" : r.user_event === "user_created_shell" ? "→ compte-coquille créé" : "")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => { setResult(null); setPreview(null); setFile(null); setSourceKey(""); }}>
              Nouveau import
            </Button>
            <Link to="/athletes"><Button size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black">Voir la liste des athlètes</Button></Link>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const cls = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    red: "text-destructive bg-destructive/5 border-destructive/20",
  }[color] || "text-foreground bg-muted/30 border";
  return (
    <div className={`p-2 rounded-md border ${cls}`}>
      <div className="text-lg font-mono font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
    </div>
  );
}

// V9.23 — Import multi-entités (Fédérations, Clubs, Coachs, Athlètes)
function MultiEntityImportView() {
  const [file, setFile] = useState(null);
  const [sourceKey, setSourceKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [officialJp2027, setOfficialJp2027] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/imports/multi-entity/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AITO_import_multi_entites_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossible de télécharger le template.");
    }
  };

  const runPreview = async () => {
    if (!file || !sourceKey.trim()) {
      toast.error("Renseigne un fichier et une clé source.");
      return;
    }
    setBusy(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_key", sourceKey.trim());
      const { data } = await api.post("/admin/imports/multi-entity/preview", fd);
      setPreview(data);
      toast.success("Preview générée");
    } catch (e) {
      showError(e, { prefix: "Preview :" });
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/imports/multi-entity/commit", {
        preview_id: preview.preview_id,
        official_jp2027_import: !!officialJp2027,
      });
      setResult(data);
      toast.success("Import multi-entités confirmé");
    } catch (e) {
      showError(e, { prefix: "Commit :" });
    } finally { setCommitting(false); }
  };

  const StatusBadge = ({ status }) => {
    const c = status === "existing" ? "bg-blue-100 text-blue-700 border-blue-200"
      : status === "new_in_batch" ? "bg-amber-100 text-amber-700 border-amber-200"
      : status === "not_found" ? "bg-destructive/10 text-destructive border-destructive/20"
      : "bg-muted text-muted-foreground";
    const label = status === "existing" ? "existant"
      : status === "new_in_batch" ? "nouveau (batch)"
      : status === "not_found" ? "non trouvé"
      : status || "—";
    return <Badge variant="outline" className={`text-[9px] ${c}`}>{label}</Badge>;
  };

  const renderAff = (aff) => (
    <span key={(aff.input || "") + Math.random()} className="inline-flex items-center gap-1 mr-1">
      <span className="text-xs">{aff.input}</span>
      <StatusBadge status={aff.resolution?.status} />
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Guide de chargement (V10 Hotfix Pilote) */}
      <Card className="p-4 space-y-2 border-[hsl(var(--aito-lagoon))]/30 bg-[hsl(var(--aito-lagoon))]/5" data-testid="multi-import-guide">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <FileSpreadsheet className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          Guide de chargement — mises à jour & résultats
        </div>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
          <li>
            <strong>Un seul fichier</strong> XLSX peut contenir jusqu'à 5 onglets :
            <code className="mx-1">FEDERATIONS</code>·<code className="mx-1">CLUBS</code>·<code className="mx-1">COACHS</code>·<code className="mx-1">ATHLETES</code>·<code className="mx-1">RESULTATS</code>.
            Onglets facultatifs — tu peux réimporter juste <code>RESULTATS</code> ou juste <code>ATHLETES</code>.
          </li>
          <li>
            <strong>Réimport = enrichissement.</strong> Un athlète reconnu par <em>Prénom + Nom</em> (ou email) ne sera <strong>jamais dupliqué</strong> :
            la fiche existante est enrichie (disciplines, palmarès IA, résultats), les affiliations sont complétées.
          </li>
          <li>
            <strong>Résultats.</strong> L'onglet <code>RESULTATS</code> ajoute des <em>Achievements</em> horodatés sur les fiches athlètes reconnues.
            Les palmarès en texte libre (colonne <code>palmares_texte</code> côté ATHLETES) sont convertis en résultats structurés par l'IA.
          </li>
          <li>
            <strong>Multi-rôle.</strong> Si une personne existante Athlète apparaît dans l'onglet <code>COACHS</code>,
            <strong> aucun doublon</strong> n'est créé : le rôle Coach est ajouté au même User et la fiche Coach canonique est rattachée.
          </li>
          <li>
            <strong>Sélection Jeux du Pacifique 2027.</strong> Ajoute dans <code>ATHLETES</code> les colonnes
            <code className="mx-1">jeux_pacifique_2027_selection</code> (OUI/NON),
            <code className="mx-1">jeux_pacifique_2027_discipline</code>,
            <code className="mx-1">jeux_pacifique_2027_source</code>,
            <code className="mx-1">jeux_pacifique_2027_reference</code>.
            Par défaut, <code>OUI</code> crée une déclaration <strong>PENDING</strong> à valider par AITO/COPF.
            Coche « Import officiel validé » ci-dessous pour créer directement des sélections <strong>SELECTED</strong>.
          </li>
          <li>
            <strong>Preview obligatoire</strong> avant confirmation. Rien n'est écrit tant que tu n'as pas cliqué « Confirmer l'import ».
            Un rollback est disponible depuis l'onglet « Imports récents ».
          </li>
        </ul>
      </Card>

      <Card className="p-4 space-y-3" data-testid="multi-entity-import-card">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Import multi-entités</div>
            <div className="text-xs text-muted-foreground">
              Onglets facultatifs : FEDERATIONS, CLUBS, COACHS, ATHLETES.
              Affiliations dans les colonnes de chaque entité (pas d'onglet AFFILIATIONS).
              Les affiliations sont créées en <b>PENDING</b> (validation ultérieure).
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="multi-download-template">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Template XLSX
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Clé source (batch)</Label>
            <Input value={sourceKey} onChange={(e) => setSourceKey(e.target.value)} placeholder="Ex : FED_JJB_2026" data-testid="multi-source-key" />
          </div>
          <div>
            <Label className="text-xs">Fichier XLSX</Label>
            <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="multi-file" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={runPreview} disabled={busy || !file || !sourceKey.trim()} data-testid="multi-run-preview">
            {busy ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Générer la preview
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-4 space-y-3" data-testid="multi-preview-card">
          <div className="text-sm font-semibold">Preview — onglets détectés : {preview.sheets_present.join(", ")}</div>
          <div className="grid sm:grid-cols-4 gap-2">
            {["federations", "clubs", "coaches", "athletes"].map((k) => {
              const s = preview[k]?.summary || {};
              return (
                <div key={k} className="border rounded p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                  <div className="text-xs">
                    <span className="font-semibold">{s.total || 0}</span> lignes ·
                    <span className="text-emerald-600 ml-1">{s.new || 0} nouv.</span> ·
                    <span className="text-blue-600 ml-1">{s.existing || 0} exist.</span>
                    {s.promote_from_athlete ? <span className="text-amber-600 ml-1">{s.promote_from_athlete} promo</span> : null}
                    {s.invalid ? <span className="text-destructive ml-1">{s.invalid} inv.</span> : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* V9.24 — Palmarès IA + RESULTATS */}
          {(preview.palmares_summary?.parsed_rows > 0 || preview.results?.summary?.total > 0) && (
            <div className="grid sm:grid-cols-2 gap-2">
              {preview.palmares_summary?.parsed_rows > 0 && (
                <div className="border rounded p-2 bg-[hsl(var(--aito-lagoon))]/5">
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Palmarès IA
                  </div>
                  <div className="text-xs mt-1">
                    <span className="font-semibold">{preview.palmares_summary.parsed_rows}</span> lignes analysées ·
                    <span className="text-emerald-600 ml-1">{preview.palmares_summary.new_entries_after_dedup} nouveaux</span> ·
                    <span className="ml-1">{preview.palmares_summary.entries_total} détectés</span>
                    {preview.palmares_summary.ai_enabled === false && <span className="text-destructive ml-1">(IA indisponible)</span>}
                  </div>
                </div>
              )}
              {preview.results?.summary?.total > 0 && (
                <div className="border rounded p-2 bg-emerald-50">
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
                    <Award className="h-3 w-3" /> Résultats structurés
                  </div>
                  <div className="text-xs mt-1">
                    <span className="font-semibold">{preview.results.summary.total}</span> lignes ·
                    <span className="text-emerald-600 ml-1">{preview.results.summary.to_create} à créer</span> ·
                    <span className="text-blue-600 ml-1">{preview.results.summary.dedup_skipped} doublons</span>
                    {preview.results.summary.not_found ? <span className="text-destructive ml-1">· {preview.results.summary.not_found} non trouvés</span> : null}
                  </div>
                </div>
              )}
            </div>
          )}

          <details className="text-xs" open>
            <summary className="cursor-pointer font-medium">Détail par entité</summary>
            <div className="mt-2 space-y-3 max-h-96 overflow-y-auto pr-1">
              {["federations", "clubs", "coaches", "athletes"].map((section) => {
                const rows = preview[section]?.rows || [];
                if (rows.length === 0) return null;
                return (
                  <div key={section}>
                    <div className="text-[10px] font-bold uppercase text-muted-foreground mb-1">{section}</div>
                    {rows.map((r, i) => (
                      <div key={i} className="border rounded p-2 mb-1" data-testid={`multi-row-${section}-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-xs">{r.name || r.full_name || `Ligne ${r.line}`}</span>
                          <StatusBadge status={r.match} />
                          {!r.valid && r.errors && (
                            <span className="text-destructive text-[10px]">Erreurs : {r.errors.join(", ")}</span>
                          )}
                        </div>
                        {r.valid && (
                          <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-1">
                            {r.club_affiliations?.length > 0 && (
                              <div>Club : {r.club_affiliations.map(renderAff)}</div>
                            )}
                            {r.federation_affiliations?.length > 0 && (
                              <div>Féd : {r.federation_affiliations.map(renderAff)}</div>
                            )}
                            {r.coach_affiliations?.length > 0 && (
                              <div>Coach : {r.coach_affiliations.map(renderAff)}</div>
                            )}
                            {r.federation_affiliation?.input && (
                              <div>Féd : {renderAff(r.federation_affiliation)}</div>
                            )}
                            {r.copf_affiliation && <Badge variant="outline" className="text-[9px]">COPF : {r.copf_affiliation}</Badge>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </details>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t">
            <label className="inline-flex items-start gap-2 cursor-pointer text-xs" data-testid="multi-official-jp2027-label">
              <input
                type="checkbox"
                checked={officialJp2027}
                onChange={(e) => setOfficialJp2027(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-[hsl(var(--aito-gold))]"
                data-testid="multi-official-jp2027"
              />
              <span>
                <strong>Import officiel validé JP 2027</strong> — les colonnes <code>jeux_pacifique_2027_selection = OUI</code> créent
                directement une sélection <strong>SELECTED</strong> au lieu de <strong>PENDING</strong>.
                <br />
                <span className="text-muted-foreground">Réservé aux imports issus d'une liste officielle de fédération / COPF.</span>
              </span>
            </label>
            <Button onClick={commit} disabled={committing} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="multi-commit">
              {committing ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Confirmer et importer
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-4 border-l-4 border-emerald-500" data-testid="multi-commit-result">
          <div className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Import multi-entités terminé
          </div>
          <div className="mt-2 text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(result.counters || {}).map(([k, v]) => (
              <div key={k} className="border rounded p-2">
                <div className="text-lg font-mono font-bold">{v}</div>
                <div className="text-[10px] uppercase tracking-wide opacity-70">{k.replaceAll("_", " ")}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Batch : {result.batch_id?.slice(0, 8)} · Les affiliations créées sont en statut <b>PENDING</b>. Les gestionnaires des Clubs/Fédérations concernés peuvent les valider dans l'onglet Affiliations de leur fiche.
          </div>
        </Card>
      )}
    </div>
  );
}


// V9.21 — Panneau propositions IA palmarès
function PalmaresAiPanel({ preview, includePalmaresAi, onToggleInclude }) {
  const summary = preview.palmares_summary || {};
  const buckets = [
    ...(preview.new || []),
    ...(preview.match_certain || []),
    ...(preview.match_probable || []),
  ];
  const rowsWithAi = buckets.filter((r) => r.palmares_ai && r.palmares_ai.raw);
  if ((summary.parsed_rows || 0) === 0 && rowsWithAi.length === 0) return null;

  const confBadge = (status) => {
    if (status === "STRUCTURÉ") {
      return <Badge className="bg-emerald-600 text-white text-[10px]">structuré</Badge>;
    }
    if (status === "À_CONFIRMER") {
      return <Badge className="bg-amber-500 text-black text-[10px]">à confirmer</Badge>;
    }
    return <Badge variant="secondary" className="text-[10px]">non structuré</Badge>;
  };

  return (
    <details className="text-xs border rounded-md p-3 bg-[hsl(var(--aito-lagoon))]/5" open data-testid="palmares-ai-panel">
      <summary className="cursor-pointer font-medium flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--aito-lagoon))]" />
        <span>Analyse IA du palmarès libre</span>
        <span className="text-muted-foreground font-normal">
          — {summary.parsed_rows || 0} ligne{(summary.parsed_rows || 0) > 1 ? "s" : ""} analysée{(summary.parsed_rows || 0) > 1 ? "s" : ""}, {summary.entries_total || 0} entrée{(summary.entries_total || 0) > 1 ? "s" : ""} détectée{(summary.entries_total || 0) > 1 ? "s" : ""}
        </span>
        {summary.ai_enabled === false && (
          <Badge variant="destructive" className="text-[10px]">IA indisponible</Badge>
        )}
      </summary>

      <div className="mt-3 grid gap-2 grid-cols-2 sm:grid-cols-4">
        <Stat label="Structuré" value={summary.structured || 0} color="emerald" />
        <Stat label="À confirmer" value={summary.to_confirm || 0} color="amber" />
        <Stat label="Non structuré" value={summary.unstructured || 0} />
        <Stat label="Entrées totales" value={summary.entries_total || 0} />
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={includePalmaresAi}
          onChange={(e) => onToggleInclude(e.target.checked)}
          data-testid="palmares-ai-include-toggle"
        />
        <span>
          <strong>Importer les achievements analysés par IA</strong>
          <span className="text-muted-foreground"> — le texte brut est toujours conservé sur la fiche</span>
        </span>
      </label>

      {rowsWithAi.length > 0 && (
        <div className="mt-3 space-y-2 max-h-96 overflow-y-auto pr-1">
          {rowsWithAi.map((r, i) => {
            const ai = r.palmares_ai;
            const name = `${r.prenom || ""} ${r.nom || ""}`.trim() || "(sans nom)";
            return (
              <div
                key={i}
                className="border rounded-md p-2 bg-background"
                data-testid={`palmares-ai-row-${i}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Award className="h-3 w-3 text-[hsl(var(--aito-gold))]" />
                  <span className="font-semibold">{name}</span>
                  {confBadge(ai.status)}
                  <span className="text-muted-foreground ml-auto">
                    {(ai.entries || []).length} entrée{(ai.entries || []).length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground italic line-clamp-2">
                  « {ai.raw} »
                </div>
                {(ai.entries || []).length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {ai.entries.map((e, k) => (
                      <li
                        key={k}
                        className="flex items-start gap-2 text-[11px] border-l-2 border-[hsl(var(--aito-lagoon))]/40 pl-2"
                      >
                        <div className="flex-1">
                          <div className="font-medium">
                            {e.title}
                            {e.format && <span className="ml-1 opacity-70">· {e.format}</span>}
                          </div>
                          <div className="text-muted-foreground">
                            {[e.year, e.medal, e.level, e.category, e.place]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </div>
                        </div>
                        {e.confidence === "structuré" ? (
                          <Badge className="bg-emerald-600 text-white text-[9px] shrink-0">OK</Badge>
                        ) : (
                          <Badge className="bg-amber-500 text-black text-[9px] shrink-0">?</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Aucune entrée structurée — le texte brut sera conservé tel quel.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 text-[10px] text-muted-foreground flex items-start gap-1">
        <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0" />
        <span>
          L'IA structure uniquement ce qui est explicitement écrit. Aucune donnée n'est inventée
          ni complétée depuis Internet. Le texte original est toujours conservé sur la fiche
          (champ <code>palmares_raw_import</code>).
        </span>
      </div>
    </details>
  );
}


// ---------- Imports récents ----------
function BatchesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/athletes/imports/batches");
      setItems(data.items || []);
    } catch (e) { showError(e, { prefix: "Historique :" }); }
    finally { setLoading(false); }
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (b) => {
    if (b.is_rolled_back) return;
    if (!window.confirm(`Annuler le batch ${b.id.slice(0, 8)} (${b.counts?.created || 0} fiches créées) ? Les profils revendiqués seront exclus.`)) return;
    try {
      const { data } = await api.post(`/admin/athletes/imports/batches/${b.id}/rollback`);
      toast.success(`Rollback : ${data.soft_deleted} soft-deleted, ${data.excluded} exclus.`);
      setKey((k) => k + 1);
    } catch (e) { showError(e, { prefix: "Rollback :" }); }
  };

  const downloadReport = async (b, fmt = "xlsx") => {
    try {
      const res = await api.get(`/admin/athletes/imports/batches/${b.id}/report.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `rapport-import-${(b.source_key || "batch")}-${b.id.slice(0, 8)}.${fmt}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { showError(e, { prefix: "Rapport :" }); }
  };

  if (loading) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Aucun import réalisé.</Card>;

  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>source_key</TableHead>
            <TableHead className="w-24">Mode</TableHead>
            <TableHead className="hidden md:table-cell">Fichier</TableHead>
            <TableHead className="hidden md:table-cell w-32">Date</TableHead>
            <TableHead className="w-40">Résultat</TableHead>
            <TableHead className="text-right w-40">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((b) => (
            <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
              <TableCell><span className="font-mono text-xs">{b.source_key}</span></TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{b.mode}</Badge></TableCell>
              <TableCell className="text-xs hidden md:table-cell truncate max-w-[24ch]">{b.filename || "—"}</TableCell>
              <TableCell className="text-xs hidden md:table-cell">{b.created_at ? new Date(b.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</TableCell>
              <TableCell className="text-xs">
                <div className="flex flex-wrap gap-1">
                  <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">+{b.counts?.created || 0}</Badge>
                  <Badge variant="outline" className="text-[10px]">~{b.counts?.updated || 0}</Badge>
                  {b.counts?.conflicts > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">⚠{b.counts.conflicts}</Badge>}
                  {b.is_rolled_back && <Badge className="bg-destructive/10 text-destructive text-[10px]">annulé</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => downloadReport(b, "xlsx")} data-testid={`batch-report-xlsx-${b.id}`} title="Télécharger le rapport XLSX">
                    <FileDown className="h-3.5 w-3.5 mr-1" />XLSX
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadReport(b, "csv")} data-testid={`batch-report-csv-${b.id}`} title="Télécharger le rapport CSV" className="hidden md:inline-flex">
                    CSV
                  </Button>
                  <Button size="sm" variant="ghost" disabled={b.is_rolled_back} onClick={() => rollback(b)} data-testid={`batch-rollback-${b.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Annuler
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ---------- Revendications ----------
function ClaimsView() {
  const { user } = useAuth();
  const isAdmin = user && (user.roles || []).includes("admin");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/athletes/claims?status=pending");
      setItems(data.items || []);
    } catch (e) { showError(e, { prefix: "Claims :" }); }
    finally { setLoading(false); }
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    const note = window.prompt(`Note (optionnelle) pour ${kind === "approve" ? "approuver" : "rejeter"} :`, "") || null;
    try {
      await api.post(`/admin/athletes/claims/${id}/${kind}`, { note });
      toast.success(kind === "approve" ? "Revendication approuvée." : "Revendication rejetée.");
      setKey((k) => k + 1);
    } catch (e) { showError(e, { prefix: kind + " :" }); }
  };

  if (loading) return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Aucune revendication en attente.</Card>;

  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Demandeur</TableHead>
            <TableHead>Athlète</TableHead>
            <TableHead className="hidden md:table-cell">Preuves</TableHead>
            <TableHead className="w-20">Conflit</TableHead>
            <TableHead className="text-right w-56">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id} data-testid={`claim-row-${c.id}`}>
              <TableCell>
                <div className="text-sm font-medium">{c.claimant_full_name || c.claimant_email || c.claimant_user_id}</div>
                {c.claimant_email && <div className="text-[11px] text-muted-foreground">{c.claimant_email}</div>}
              </TableCell>
              <TableCell><a href={`/athletes/${c.athlete_id}`} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline">{c.athlete_id.slice(0, 12)}…</a></TableCell>
              <TableCell className="text-xs hidden md:table-cell">
                <code className="text-[10px] bg-muted p-1 rounded">{JSON.stringify(c.proof || {}).slice(0, 60)}</code>
              </TableCell>
              <TableCell>
                {c.is_conflict ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">Conflit</Badge> : <Badge variant="outline" className="text-[10px]">Simple</Badge>}
              </TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="ghost" className="text-emerald-700" disabled={c.is_conflict && !isAdmin} onClick={() => act(c.id, "approve")} data-testid={`claim-approve-${c.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approuver
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" disabled={c.is_conflict && !isAdmin} onClick={() => act(c.id, "reject")} data-testid={`claim-reject-${c.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Rejeter
                  </Button>
                </div>
                {c.is_conflict && !isAdmin && <div className="text-[10px] text-muted-foreground mt-1">Admin requis</div>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}


// ---------- Tableau de pilotage (widget) ----------
function AthletesDashboardWidget() {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/athletes/dashboard")
      .then((r) => { if (!cancelled) setCounts(r.data); })
      .catch(() => { if (!cancelled) setCounts({ imports_recent: 0, duplicates_pending: 0, claims_pending: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !counts) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="athletes-dashboard-widget">
      <PilotTile
        to="/admin/athletes/imports?tab=batches"
        icon={ClipboardList}
        label="Imports récents (30j)"
        value={counts.imports_recent}
        color="hsl(var(--aito-lagoon))"
        testid="pilot-imports"
      />
      <PilotTile
        to="/admin/athletes/duplicates"
        icon={Copy}
        label="Doublons à traiter"
        value={counts.duplicates_pending}
        color="hsl(var(--aito-energy))"
        testid="pilot-duplicates"
      />
      <PilotTile
        to="/admin/athletes/imports?tab=claims"
        icon={UserCheck}
        label="Revendications en attente"
        value={counts.claims_pending}
        color="hsl(var(--aito-gold))"
        testid="pilot-claims"
      />
    </div>
  );
}

function PilotTile({ to, icon: Icon, label, value, color, testid }) {
  return (
    <Link to={to} className="block group" data-testid={testid}>
      <Card className="p-4 hover:border-[hsl(var(--aito-lagoon))] transition">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-2xl font-display font-bold" style={{ color: value > 0 ? color : undefined }}>{value}</div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
